import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PortalV2Layout from './PortalV2Layout';
import {
  Activity, RefreshCw, AlertCircle, CheckCircle, Clock, XCircle,
  ChevronRight, Inbox,
} from 'lucide-react';

const fmtDateTime = (s) => s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) : '—';

const ClientV2Activity = () => {
  const { authFetch } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchActivity = async () => {
    setLoading(true); setError(null);
    try {
      const r = await authFetch('/api/portal/activity?limit=25');
      if (!r.ok) throw new Error('Failed to fetch activity');
      setActivities((await r.json()).activities || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchActivity(); /* eslint-disable-next-line */ }, []);

  const Icon = ({ a }) => {
    if (a.type === 'received') return <Inbox size={14} strokeWidth={1.5} color="#60a5fa" />;
    if (a.success) return <CheckCircle size={14} strokeWidth={1.5} color="var(--pv2-green-text)" />;
    return <XCircle size={14} strokeWidth={1.5} color="var(--pv2-amber-text)" />;
  };

  const titleFor = (a) => {
    if (a.type === 'received') return 'Documents Received';
    if (a.success) return 'Successfully Served';
    return a.serve_type || 'Attempt Made';
  };

  const badgeColor = (a) => {
    if (a.type === 'received') return '#60a5fa';
    if (a.success) return 'var(--pv2-green-text)';
    return 'var(--pv2-amber-text)';
  };

  return (
    <PortalV2Layout
      eyebrow="Operations"
      title="Activity Feed"
      subtitle="Recent updates across all your jobs"
      crumbs={<strong>Activity</strong>}
      action={
        <button onClick={fetchActivity} disabled={loading} className="pv2-btn pv2-btn-ghost" data-testid="pv2-refresh-activity">
          <RefreshCw size={14} strokeWidth={1.5} className={loading ? 'pv2-skeleton' : ''} /> Refresh
        </button>
      }
    >
      {error && <div className="pv2-alert pv2-alert-red"><AlertCircle size={14} /><span>{error}</span></div>}

      <div className="pv2-card" style={{ overflow: 'hidden' }}>
        {loading ? <div style={{ padding: 48, textAlign: 'center', color: 'var(--pv2-text-muted)' }}>Loading activity…</div>
          : activities.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center' }}>
              <Activity size={28} style={{ opacity: 0.3, marginBottom: 12 }} />
              <div style={{ fontSize: 14, color: 'var(--pv2-text-muted)' }}>No activity yet</div>
              <div style={{ fontSize: 12, color: 'var(--pv2-text-subtle)', marginTop: 4 }}>Updates will appear here as your jobs progress</div>
            </div>
          ) : (
            <div>
              {activities.map((a, i) => {
                const c = badgeColor(a);
                return (
                  <Link key={i} to={`/portal/jobs/${a.job?.job_id}`}
                    data-testid={`pv2-activity-${i}`}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '16px 20px', borderBottom: '1px solid var(--pv2-ink-line-soft)', textDecoration: 'none', color: 'inherit', ...(a.job?.is_rush ? { background: 'rgba(248, 113, 113, 0.07)', boxShadow: 'inset 3px 0 0 0 var(--pv2-red-bright)' } : {}) }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 14, fontWeight: 500 }}>{a.job?.recipient_name}</span>
                        <span className="pv2-pill" style={{ color: c }}>{titleFor(a)}</span>
                      </div>
                      {a.job?.case_title && <div style={{ fontSize: 12, color: 'var(--pv2-text-subtle)', marginTop: 4, fontStyle: 'italic' }}>{a.job.case_title}</div>}
                      {a.description && <div style={{ fontSize: 13, color: 'var(--pv2-text-muted)', marginTop: 6, lineHeight: 1.5 }}>&ldquo;{a.description}&rdquo;</div>}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 8, fontSize: 11, color: 'var(--pv2-text-muted)' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                          <Clock size={10} strokeWidth={1.5} /> {fmtDateTime(a.timestamp)}
                        </span>
                        {a.server_name && <span>Server: {a.server_name}</span>}
                        {a.job?.job_number && <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>#{a.job.job_number}</span>}
                      </div>
                    </div>
                    <ChevronRight size={14} color="var(--pv2-text-subtle)" style={{ marginTop: 8 }} />
                  </Link>
                );
              })}
            </div>
          )}
      </div>

      {activities.length > 0 && (
        <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--pv2-text-subtle)', marginTop: 16 }}>
          Showing last {activities.length} activities
        </p>
      )}
    </PortalV2Layout>
  );
};

export default ClientV2Activity;
